import React, { useState, useEffect, Fragment } from "react";
import DataTable from "react-data-table-component";
import { Badge, Card, CardBody, DropdownItem } from "reactstrap";
import ActionOptions from "../../Common/Component/ActionOptions";
import StatusBadge from "../../Common/Component/StatusBadge";
import {
  getDriverTypeDeductions,
  deletSchemeDeductions
} from "../../../Services/Driver.service";
import { showAlert } from "../../../Services/Shared.service";
import { Spinner } from "../../../AbstractElements";
import TariffModal from "../../../CommonElements/TariffModal/TariffModal";
import SingleDeductionForm from "../Forms/SingleDeductionForm";

const DeductionList = ({ scheme, refreshTrigger }) => {
  const [deductions, setDeductions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isEditModal, setIsEditModal] = useState(false);
  const [selectedDeduction, setSelectedDeduction] = useState(null);

  useEffect(() => {
    if (scheme) {
      fetchDeductions();
    }
  }, [scheme, refreshTrigger]);

  const fetchDeductions = async () => {
    setLoading(true);
    try {
      const resp = await getDriverTypeDeductions(scheme.id, scheme.serviceCityId);
      if (resp.status === 200) {
        setDeductions(resp.data || []);
      }
    } catch (e) {
      console.error("Error fetching deductions", e);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (row) => {
    setSelectedDeduction(row);
    setIsEditModal(true);
  };

  const handleDeleteDeduction = (row) => {
    deletSchemeDeductions({ schemeDeductionId: row.id, isDeleted: !row.isDeleted })
      .then(() => {
        showAlert("success", "Status updated successfully");
        fetchDeductions();
      })
      .catch((error) => {
        console.error("Error toggling deduction status:", error);
        const msg =
          error.response?.data?.message ||
          "Failed to update deduction status.";
        showAlert("error", msg);
      });
  };

  const columns = [
    {
      name: "Deduction Type",
      selector: (row) => row.driverDeductionName || "-",
      sortable: true,
    },
    {
      name: "Value Type",
      selector: (row) => (row.valueType === "P" ? "Percentage" : "Flat Amount"),
      sortable: true,
    },
    {
      name: "Value",
      selector: (row) => (row.valueType === "P" ? `${row.value}%` : `₹${row.value}`),
      sortable: true,
    },
    {
      name: "Calc Type",
      selector: (row) => row.calculateType || "-",
      sortable: true,
    },
    {
      name: "Booking Type",
      selector: (row) => row.bookingTypeDeduction || "All",
      sortable: true,
    },
    {
      name: "Status",
      cell: (row) => (
        <StatusBadge
          isDeleted={row.isDeleted}
          onClick={() => handleDeleteDeduction(row)}
        />
      ),
    },
    {
      name: "Actions",
      cell: (row) => (
        <ActionOptions>
          <Fragment>
            <DropdownItem
              className="border-0"
              onClick={() => handleEdit(row)}
            >
              <i className="fa fa-pencil"></i> Edit
            </DropdownItem>
            <DropdownItem
              className="border-0"
              onClick={() => handleDeleteDeduction(row)}
            >
              <i className={`fa ${row.isDeleted ? "fa-refresh" : "fa-trash-o"}`}></i>
              {row.isDeleted ? " Recover" : " Delete"}
            </DropdownItem>
          </Fragment>
        </ActionOptions>
      ),
    }
  ];

  const customStyles = {
    headRow: {
      style: {
        backgroundColor: "#E1F4E4",
        minHeight: "50px",
      },
    },
    headCells: {
      style: {
        color: "#626262",
        fontWeight: "700",
        fontSize: "15px",
      },
    },
    rows: {
      style: {
        "&:hover": {
          backgroundColor: "#f5f5f5 !important",
        },
      },
    },
  };

  return (
    <div className="mt-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5 className="mb-0 fw-bold" style={{ color: "#444" }}>Scheme Deductions</h5>
      </div>

      <Card style={{ borderRadius: "15px", border: "none", boxShadow: "0 4px 20px rgba(0,0,0,0.08)" }}>
        <CardBody className="p-0">
          {loading ? (
            <div className="p-5 text-center">
              <Spinner size="sm" />
            </div>
          ) : (
            <DataTable
              columns={columns}
              data={deductions}
              customStyles={customStyles}
              pagination
              noDataComponent={
                <div className="p-5 text-center text-muted">
                  No deduction rules defined for this scheme yet.
                </div>
              }
            />
          )}
        </CardBody>
      </Card>

      {/* Edit Modal */}
      {isEditModal && (
        <TariffModal
          isOpen={isEditModal}
          size="lg"
          centered
          toggler={() => setIsEditModal(false)}
          title="Edit Deduction Rule"
        >
          <SingleDeductionForm
            deduction={selectedDeduction}
            onClose={() => setIsEditModal(false)}
            onRefresh={fetchDeductions}
          />
        </TariffModal>
      )}
    </div>
  );
};

export default DeductionList;
